import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { scrubOffset, scrubPassedDeadZone, scrubStop, type ScrubAnchor } from './scrub'

/**
 * The press of a scrub control (`ScrubButton`, and `WorldButton` with `scrub`): press and drag steps
 * through `stops` live (src/play/scrub.ts: right and up are more, 28 px a stop); a press that never
 * leaves the dead zone is a tap. The pointer is captured and its events stopped, so nothing reaches
 * the canvas. Returns the button's handlers and whether it is held (`down`) or dragging.
 *
 * - `onTap`: a tap, or Enter / Space on the focused button.
 * - `onDragStart`: once, when a press leaves the dead zone (before the first `onChange`).
 * - `onAdjustingChange`: true while held (`adjustWhile: 'held'`) or only once dragging
 *   (`'dragging'`), false on release; false on unmount.
 */
export function useScrubPress({ stops, value, onChange, onTap, onDragStart, onAdjustingChange, adjustWhile = 'held' }: {
  stops: number
  value: number
  onChange: (stop: number) => void
  onTap?: () => void
  onDragStart?: () => void
  onAdjustingChange?: (adjusting: boolean) => void
  adjustWhile?: 'held' | 'dragging'
}) {
  const valueRef = useRef(value)
  valueRef.current = value
  const press = useRef<{ id: number; x: number; y: number; dragging: boolean; anchor: ScrubAnchor; stop: number } | null>(null)
  const [down, setDown] = useState(false)
  const [dragging, setDragging] = useState(false)
  const end = () => {
    press.current = null
    setDown(false)
    setDragging(false)
  }

  const adjustingRef = useRef(onAdjustingChange)
  adjustingRef.current = onAdjustingChange
  const adjusting = adjustWhile === 'held' ? down : dragging
  const reported = useRef(false)
  useEffect(() => {
    if (adjusting === reported.current) return
    reported.current = adjusting
    adjustingRef.current?.(adjusting)
  }, [adjusting])
  useEffect(() => () => { if (reported.current) adjustingRef.current?.(false) }, [])

  const handlers = {
    onPointerDown(e: ReactPointerEvent<HTMLElement>) {
      e.stopPropagation()
      if (press.current || (e.pointerType === 'mouse' && e.button !== 0)) return
      e.preventDefault()
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
      const stop = Math.round(valueRef.current)
      press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, dragging: false, anchor: { stop, offset: 0 }, stop }
      setDown(true)
    },
    onPointerMove(e: ReactPointerEvent<HTMLElement>) {
      const p = press.current
      if (!p || p.id !== e.pointerId) return
      // Something round it took the press this move (a page band starting to scroll): not ours.
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
      e.stopPropagation()
      const dx = e.clientX - p.x
      const dy = e.clientY - p.y
      if (!p.dragging) {
        if (!scrubPassedDeadZone(dx, dy)) return
        p.dragging = true
        setDragging(true)
        onDragStart?.()
      }
      const r = scrubStop(p.anchor, scrubOffset(dx, dy), stops)
      p.anchor = r.anchor
      if (r.stop !== p.stop) {
        p.stop = r.stop
        onChange(r.stop)
      }
    },
    onPointerUp(e: ReactPointerEvent<HTMLElement>) {
      const p = press.current
      if (!p || p.id !== e.pointerId) return
      e.stopPropagation()
      try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
      end()
      if (!p.dragging) onTap?.()
    },
    onPointerCancel(e: ReactPointerEvent<HTMLElement>) { if (press.current?.id === e.pointerId) end() },
    onLostPointerCapture(e: ReactPointerEvent<HTMLElement>) { if (press.current?.id === e.pointerId) end() },
    onClick(e: ReactMouseEvent<HTMLElement>) {
      e.stopPropagation()
      // Pointer presses are handled above; a click with no pointer (Enter, Space) is a tap.
      if (e.detail === 0) onTap?.()
    },
    onContextMenu(e: ReactMouseEvent<HTMLElement>) { e.preventDefault() },
  }
  return { down, dragging, handlers }
}
